import { useQuery } from '@tanstack/react-query';
import type { PublishedSwimlaneResponse } from '../published/types';

interface UseExportSwimlaneParams {
  viewId: number;
  visualizationId: number;
  token: string;
}

export type UseExportSwimlaneResult =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'success'; data: PublishedSwimlaneResponse };

/**
 * React Query hook for fetching visualization data using export token
 * Used by export route for token-authenticated data access
 */
export function useExportSwimlane({
  viewId,
  visualizationId,
  token,
}: UseExportSwimlaneParams): UseExportSwimlaneResult {
  const query = useQuery({
    queryKey: ['export-swimlane', viewId, visualizationId, token],
    queryFn: async () => {
      const url = `/export-api/v1/roadmap/view/${viewId}/visualization/${visualizationId}/swimlane?token=${encodeURIComponent(token)}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          throw new Error('Invalid or expired export token');
        }
        if (response.status === 404) {
          throw new Error('Visualization not found');
        }
        throw new Error(`Failed to fetch visualization: ${response.statusText}`);
      }

      const data = await response.json();
      return data as PublishedSwimlaneResponse;
    },
    retry: false, // Don't retry on failure (token might be expired)
    staleTime: 0, // Always fetch fresh data
  });

  if (query.isLoading) {
    return { status: 'loading' };
  }

  if (query.isError) {
    return {
      status: 'error',
      message: query.error instanceof Error ? query.error.message : 'Unknown error',
    };
  }

  if (query.data) {
    return { status: 'success', data: query.data };
  }

  return { status: 'loading' };
}
